/**
 * ⚠️ TEMPORARY STUB - DELETE when @optima-stride-ai/platform available
 * 
 * AuthenticationStubService
 * 
 * Provides mock authentication functionality until the real platform service is available.
 * This stub simulates user authentication and session management.
 * 
 * Real Platform Implementation Will Provide:
 * - OAuth/SSO authentication flow
 * - Token management (JWT, refresh tokens)
 * - User session management
 * - Multi-factor authentication
 * - Password management
 * - Session expiry handling
 * 
 * @todo DELETE this entire stub when @optima-stride-ai/platform is available
 * @todo Replace with import from '@optima-stride-ai/platform/authentication'
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Mock User Interface
 * Simplified user object for stub purposes
 */
export interface StubUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatarUrl?: string;
  roles: string[];
  tenantId: string;
}

/**
 * Mock Login Credentials
 */
export interface StubLoginCredentials {
  email: string;
  password: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationStubService {
  /**
   * Current authenticated user
   * Null when not authenticated
   */
  private currentUserSubject: BehaviorSubject<StubUser | null>;
  
  /**
   * Observable stream of authentication state changes
   */
  public currentUser$: Observable<StubUser | null>;
  
  /**
   * Authentication status
   */
  private isAuthenticatedSubject: BehaviorSubject<boolean>;
  public isAuthenticated$: Observable<boolean>;

  /**
   * Default mock user for demo purposes
   */
  private readonly DEFAULT_USER: StubUser = {
    id: 'user-001',
    email: 'demo@optimastride.ai',
    firstName: 'Demo',
    lastName: 'User',
    displayName: 'Demo User',
    avatarUrl: './assets/users/demo-avatar.png',
    roles: ['admin', 'user'],
    tenantId: 'tenant-acme-001'
  };

  constructor() {
    // Initialize as authenticated with default user for demo
    this.currentUserSubject = new BehaviorSubject<StubUser | null>(this.DEFAULT_USER);
    this.currentUser$ = this.currentUserSubject.asObservable();
    
    this.isAuthenticatedSubject = new BehaviorSubject<boolean>(true);
    this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  }

  /**
   * Set the current user (for demo initialization)
   * 
   * @param user - User object to set as current
   */
  setCurrentUser(user: StubUser | null): void {
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(user !== null);
  }
  getCurrentSocketUser( ){
    return {
      userId: localStorage.getItem('userId') || 'unknown-user',
      tenantId: localStorage.getItem('tenantId') || 'unknown-tenant'
    }
  }

  /**
   * Get current authenticated user
   * 
   * @returns Current user or null
   */
  getCurrentUser(): StubUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   * 
   * @returns true if authenticated
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Mock login
   * In stub, always succeeds and uses default user
   * 
   * @param credentials - Login credentials (ignored in stub)
   * @returns Promise that resolves when login complete
   */
  async login(credentials: StubLoginCredentials): Promise<void> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Generate random UUIDs for tenantId and userId
    const tenantId = this.generateUUID();
    const userId = this.generateUUID();
    
    // Save to localStorage
    localStorage.setItem('tenantId', tenantId);
    localStorage.setItem('userId', userId);
    
    // Set authenticated user
    this.setCurrentUser(this.DEFAULT_USER);
    
    // TODO: Real platform would:
    // - Call authentication API
    // - Validate credentials
    // - Retrieve user profile
    // - Store authentication tokens
    // - Set up session
  }

  /**
   * Mock logout
   * 
   * @returns Promise that resolves when logout complete
   */
  async logout(): Promise<void> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Clear user
    this.setCurrentUser(null);
    
    // TODO: Real platform would:
    // - Invalidate authentication tokens
    // - Clear session data
    // - Redirect to login page
    // - Clear cached user data
  }

  /**
   * Get authentication token (mock)
   * 
   * @returns Mock JWT token
   */
  getToken(): string | null {
    if (!this.isAuthenticated()) {
      return null;
    }
    
    // Return mock token
    return 'mock-jwt-token-' + this.currentUserSubject.value?.id;
  }

  /**
   * Refresh authentication token (mock)
   * 
   * @returns Promise that resolves with new token
   */
  async refreshToken(): Promise<string> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const token = this.getToken();
    
    // TODO: Real platform would:
    // - Call refresh token API
    // - Update stored tokens
    // - Handle refresh token expiry
    
    return token || 'mock-jwt-token-refreshed';
  }

  /**
   * Generate a random UUID v4
   * 
   * @returns UUID string
   */
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
